import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CALENDAR_COPY } from "./calendar-subscription-words";
import type { Act } from "./use-calendar-box";

const HOW_TO = [
  ["Google Calendar", "Other calendars, From URL"],
  ["Apple Calendar", "File, New Calendar Subscription"],
  ["Outlook", "Add calendar, Subscribe from web"],
] as const;

export function AddressPart({ address }: { address: string }) {
  const copy = CALENDAR_COPY;
  const ref = useRef<HTMLInputElement>(null);
  const [copied, setCopied] = useState("");
  async function copyAddress() {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(copy.copied);
    } catch {
      ref.current?.select();
      setCopied(copy.selectToCopy);
    }
  }
  return (
    <div className="grid gap-4">
      <div className="grid gap-1.5">
        <label htmlFor="calendar-subscription-address" className="text-ui font-medium">{copy.address}</label>
        <div className="flex flex-wrap items-center gap-2">
          <Input
            ref={ref}
            id="calendar-subscription-address"
            data-calendar-address
            readOnly
            className="min-w-0 flex-1 basis-full sm:basis-0"
            value={address}
            onFocus={(event) => event.currentTarget.select()}
          />
          <Button variant="outline" onClick={() => void copyAddress()}>{copy.copy}</Button>
          {copied && <span role="status" className="text-meta text-muted-foreground">{copied}</span>}
        </div>
        <p className="text-meta text-muted-foreground">{copy.shownOnce}</p>
      </div>
      <div className="grid gap-1.5">
        <p className="text-ui font-medium">{copy.inApp}</p>
        <dl className="grid gap-1.5 text-ui">
          {HOW_TO.map(([app, path]) => (
            <div key={app} className="grid gap-0.5 sm:grid-cols-[9rem_1fr] sm:gap-3">
              <dt className="font-medium">{app}</dt>
              <dd className="text-muted-foreground">{path}</dd>
            </div>
          ))}
        </dl>
      </div>
    </div>
  );
}

export function ChoicePart({ choice, onChoose }: { choice: Act | null; onChoose: (act: Act) => void }) {
  const copy = CALENDAR_COPY;
  return (
    <fieldset className="grid gap-2 text-ui">
      <legend className="sr-only">{copy.choice}</legend>
      {(
        [
          ["replace", copy.replace, copy.replaceNote],
          ["stop", copy.stop, copy.stopNote],
        ] as const
      ).map(([value, label, note]) => (
        <label key={value} className="flex items-start gap-2">
          <input type="radio" name="calendar-address-act" className="mt-1" checked={choice === value} onChange={() => onChoose(value)} />
          <span>
            <span className="font-medium text-foreground">{label}</span>
            <span className="block text-meta text-muted-foreground">{note}</span>
          </span>
        </label>
      ))}
    </fieldset>
  );
}
